// src/components/UserDashboardPOS.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Icon,
  Avatar,
  Badge,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Divider,
} from "@chakra-ui/react";
import {
  FaCashRegister,
  FaShoppingCart,
  FaReceipt,
  FaCreditCard,
  FaExchangeAlt,
  FaWarehouse,
  FaBoxOpen,
  FaChartLine,
  FaClock,
  FaUndoAlt,
  FaUsers,
  FaFileInvoice,
  FaTools,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";
import { useUser } from "../context/UserContext";
import WhatsNewModal from "../components/WhatsNewModal";

// Re-usable KPI card component
const KPIBox = ({ icon, label, value, helpText, color }) => {
  const cardBg = useColorModeValue("whiteAlpha.900", "gray.800");
  const glassBg = useColorModeValue(`${color}.50`, `${color}.700`);
  const borderClr = useColorModeValue(`${color}.100`, `${color}.600`);
  const borderHover = useColorModeValue(`${color}.200`, `${color}.500`);
  const labelClr = useColorModeValue("gray.500", "gray.400");
  const valueClr = useColorModeValue("gray.700", "whiteAlpha.900");
  const helpClr = useColorModeValue("gray.400", "gray.500");

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      p={5}
      shadow="lg"
      backdropFilter="blur(8px)"
      border="1px solid"
      borderColor={borderClr}
      transition="all 0.18s ease"
      _hover={{
        boxShadow: `0 8px 28px rgba(0,0,0,0.08)`,
        cursor: "pointer",
        borderColor: borderHover,
        transform: "translateY(-3px)",
      }}
    >
      <Flex align="center" gap={4}>
        <Box
          bg={glassBg}
          color={`${color}.600`}
          p={3}
          borderRadius="full"
          minW="44px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={icon} boxSize={5} />
        </Box>
        <Stat flex="1">
          <StatLabel fontSize="sm" color={labelClr}>
            {label}
          </StatLabel>
          <StatNumber fontSize="xl" color={valueClr}>
            {value}
          </StatNumber>
          <StatHelpText color={helpClr}>{helpText}</StatHelpText>
        </Stat>
      </Flex>
    </Box>
  );
};

const UserDashboardPOS = () => {
  const { loggedInUser } = useUser();

  // What's New modal state
  const [isWhatsNewOpen, setWhatsNewOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    // Check if user has seen the current version
    const hasSeenCurrentVersion = localStorage.getItem(
      "novatra_whats_new_viewed"
    );
    if (!hasSeenCurrentVersion) {
      setWhatsNewOpen(true);
    }
  }, []);

  const handleCloseWhatsNew = () => {
    if (dontShowAgain) {
      localStorage.setItem("novatra_whats_new_viewed", "1.2.0");
    }
    setWhatsNewOpen(false);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const boxBg = useColorModeValue("white", "gray.800");
  const borderClr = useColorModeValue("gray.100", "gray.700");
  const headingClr = useColorModeValue("gray.800", "whiteAlpha.900");
  const subTextClr = useColorModeValue("gray.500", "gray.400");

  return (
    <Box p={{ base: 4, md: 6 }} minH="100vh">
      {/* What's New Modal */}
      <WhatsNewModal
        isOpen={isWhatsNewOpen}
        onClose={handleCloseWhatsNew}
        version="1.2.0"
        releaseDate="4 November 2025"
        features={[
          {
            title: "Enhanced Dashboard Analytics",
            description:
              "Real-time data visualization with improved performance metrics",
          },
          {
            title: "Smart Inventory Management",
            description: "Automated stock alerts and reorder suggestions",
          },
          {
            title: "Advanced Payment Processing",
            description:
              "Support for multiple payment gateways and split payments",
          },
          {
            title: "Improved User Experience",
            description:
              "Redesigned interface with better accessibility and responsiveness",
          },
        ]}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={setDontShowAgain}
      />

      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        mb={8}
        flexDir={{ base: "column", md: "row" }}
        gap={4}
      >
        <Box textAlign={{ base: "center", md: "left" }}>
          <Heading size="lg" color={headingClr}>
            {getGreeting()}, {loggedInUser?.name || "Merchant"} 👋
          </Heading>
          <Text fontSize="sm" color={subTextClr} mt={1}>
            Overview — Live POS overview for your stores
          </Text>
        </Box>

        <Flex align="center" gap={3}>
          <Badge
            colorScheme="green"
            fontSize="0.85em"
            px={3}
            py={1}
            borderRadius="full"
          >
            Live
          </Badge>
          <Avatar
            name={loggedInUser?.name || "Merchant"}
            size="sm"
            src={loggedInUser?.avatar || "/profile.png"}
          />
        </Flex>
      </Flex>

      {/* KPI Grid */}
      <Grid
        templateColumns={{
          base: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        }}
        gap={6}
        mb={8}
      >
        <KPIBox
          icon={FaCashRegister}
          label="Total Sales (Today)"
          value="₹ 1,24,760"
          helpText="Up 8% vs yesterday"
          color="teal"
        />
        <KPIBox
          icon={FaShoppingCart}
          label="Orders (Today)"
          value="342"
          helpText="Avg ticket ₹ 365"
          color="purple"
        />
        <KPIBox
          icon={FaUsers}
          label="Active POS Terminals"
          value="6"
          helpText="All terminals online"
          color="green"
        />
        <KPIBox
          icon={FaCreditCard}
          label="Pending Payments"
          value="₹ 4,200"
          helpText="Manual follow-up required"
          color="orange"
        />
        <KPIBox
          icon={FaWarehouse}
          label="Inventory Alerts"
          value="12"
          helpText="Low-stock SKUs"
          color="red"
        />
        <KPIBox
          icon={FaBoxOpen}
          label="Items Low Stock"
          value="28"
          helpText="Reorder recommended"
          color="cyan"
        />
        <KPIBox
          icon={FaExchangeAlt}
          label="Reconciled Today"
          value="₹ 98,540"
          helpText="85% reconciled"
          color="blue"
        />
        <KPIBox
          icon={FaUndoAlt}
          label="Refunds (Today)"
          value="6"
          helpText="Total ₹ 2,100"
          color="pink"
        />
        <KPIBox
          icon={FaFileInvoice}
          label="Open Orders"
          value="14"
          helpText="Being prepared / delivery"
          color="gray"
        />
        <KPIBox
          icon={FaChartLine}
          label="Net Revenue (MTD)"
          value="₹ 12,45,900"
          helpText="Including taxes & fees"
          color="purple"
        />
        <KPIBox
          icon={FaClock}
          label="Avg Checkout Time"
          value="1m 12s"
          helpText="Faster is better"
          color="teal"
        />
        <KPIBox
          icon={FaReceipt}
          label="Tickets (This Month)"
          value="9,842"
          helpText="Dine-in + takeaway"
          color="blue"
        />
        <KPIBox
          icon={FaTools}
          label="Pending Reconciliations"
          value="2"
          helpText="Needs accounting approval"
          color="orange"
        />
        <KPIBox
          icon={FaChartLine}
          label="Avg Order Value"
          value="₹ 365"
          helpText="Today's average"
          color="green"
        />
      </Grid>

      {/* Shortcuts / Quick Actions */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
        <Box
          bg={boxBg}
          p={4}
          rounded="2xl"
          shadow="base"
          border="1px solid"
          borderColor={borderClr}
        >
          <Heading size="md" mb={3} color={headingClr}>
            Quick Actions
          </Heading>
          <Flex direction="column" gap={3}>
            <Button leftIcon={<FaCashRegister />} variant="solid" size="sm">
              Open POS
            </Button>
            <Button leftIcon={<FaShoppingCart />} variant="outline" size="sm">
              New Order
            </Button>
            <Button leftIcon={<FaWarehouse />} variant="ghost" size="sm">
              Inventory
            </Button>
          </Flex>
        </Box>

        <Box
          bg={boxBg}
          p={4}
          rounded="2xl"
          shadow="base"
          border="1px solid"
          borderColor={borderClr}
        >
          <Heading size="md" mb={3} color={headingClr}>
            Recent Alerts
          </Heading>
          <Text color={subTextClr} fontSize="sm">
            12 SKUs low in stock • 2 terminals disconnected • 1 payment dispute
          </Text>
        </Box>

        <Box
          bg={boxBg}
          p={4}
          rounded="2xl"
          shadow="base"
          border="1px solid"
          borderColor={borderClr}
        >
          <Heading size="md" mb={3} color={headingClr}>
            Reconciliation
          </Heading>
          <Text color={subTextClr} fontSize="sm" mb={3}>
            Reconciled ₹ 98,540 of ₹ 1,15,000 expected today
          </Text>
          <Button size="sm" variant="outline" leftIcon={<FaFileInvoice />}>
            Run Reconciliation
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

export default UserDashboardPOS;
